import { useState, useRef } from "react";
import { base44 } from "@/api/base44Client";
import SEO from "../components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles,
  AlertCircle,
  ArrowRight,
  Loader2,
  FileCheck,
  ChevronRight,
  Mail,
  Download,
  Upload,
  X,
  FileText,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import { toast } from "sonner";
import ValidationHero from "@/components/validation/ValidationHero";
import ValidationAIImpact from "@/components/validation/ValidationAIImpact";
import ValidationWhatYouGet from "@/components/validation/ValidationWhatYouGet";
import ValidationWhoItsFor from "@/components/validation/ValidationWhoItsFor";
import ValidationEvalProcess from "@/components/validation/ValidationEvalProcess";
import ValidationNextSteps from "@/components/validation/ValidationNextSteps";
import IntakeExpectations from "@/components/validation/IntakeExpectations";
import { getAttribution } from "@/lib/homeAttribution";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { getVariant, getVisitorId, logExperimentEvent } from "@/lib/homeExperiment";

const ORG_TYPES = [
  { value: "CLIA lab", label: "CLIA Laboratory" },
  { value: "ISO 17025 lab", label: "ISO 17025 Laboratory" },
  { value: "GMP pharmaceutical", label: "GMP Pharmaceutical" },
  { value: "Biotech startup", label: "Biotech / Startup" },
  { value: "Contract research lab", label: "Contract Research / Testing Lab" },
  { value: "Medical device manufacturer", label: "Medical Device Manufacturer" },
  { value: "Other regulated organization", label: "Other regulated organization" },
];

const VALIDATION_TYPES = [
  { value: "Equipment IQ/OQ/PQ", label: "Equipment Qualification (IQ/OQ/PQ)" },
  { value: "Analytical method validation", label: "Analytical Method Validation" },
  { value: "Process validation", label: "Process / Manufacturing Validation" },
  { value: "Computer system validation (CSV)", label: "Software / Computer System Validation (CSV)" },
  { value: "Cleaning validation", label: "Cleaning Validation" },
  { value: "Other validation activity", label: "Other validation activity" },
];

function ResultView({ result, name, formData }) {
  const nextSteps = result.draftNextSteps || [];
  
  const exportToPDF = () => {
    const sanitize = (str) => {
      if (!str) return '';
      return str
        .replace(/²/g, '^2').replace(/³/g, '^3')
        .replace(/±/g, '+/-')
        .replace(/\u2264/g, '<=').replace(/\u2265/g, '>=')
        .replace(/\u2013/g, '-').replace(/\u2014/g, '--')
        .replace(/\u2018|\u2019/g, "'").replace(/\u201C|\u201D/g, '"')
        .replace(/\u00B0/g, ' deg').replace(/\u00B5/g, 'u')
        .replace(/[^\x00-\xFF]/g, '?');
    };

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    const LH = 6;
    let y = 20;

    const checkPage = (needed = LH) => {
      if (y + needed > pageHeight - 20) { doc.addPage(); y = 20; }
    };

    const writeBodyLines = (text, indentX = margin) => {
      const lines = doc.splitTextToSize(sanitize(text), maxWidth - (indentX - margin));
      lines.forEach((line) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        checkPage(LH);
        doc.text(line, indentX, y);
        y += LH;
      });
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(0);
    doc.text('AI Validation Assessment', margin, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(sanitize(`Prepared for ${name} • ${new Date().toLocaleDateString()}`), margin, y);
    y += 14;

    const sectionHeading = (title) => {
      checkPage(14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(0);
      doc.text(title, margin, y);
      y += 8;
    };

    sectionHeading('Project Details');
    if (formData.orgType) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(30, 30, 30);
      doc.text(sanitize(`Organization: ${formData.orgType}`), margin, y); y += LH;
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(30, 30, 30);
    doc.text(sanitize(`Validation Type: ${formData.validationType}`), margin, y); y += LH;
    if (formData.primaryChallenge) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(30, 30, 30);
      doc.text('Primary Challenge:', margin, y); y += LH;
      writeBodyLines(formData.primaryChallenge, margin + 5);
    }
    if (formData.dataReadiness) {
      const readinessMap = { clean: 'Clean and structured', some_cleaning: 'Some cleaning needed', raw: 'Raw and disparate', unsure: 'Unsure' };
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(30, 30, 30);
      checkPage(LH);
      doc.text(sanitize(`Data Readiness: ${readinessMap[formData.dataReadiness] || formData.dataReadiness}`), margin, y);
      y += LH;
    }
    y += 6;

    sectionHeading('Validation Status Summary');
    writeBodyLines(result.draftSummary);
    y += 8;

    checkPage(14);
    sectionHeading('Suggested Next Steps');
    nextSteps.forEach((step, i) => {
      const fullText = sanitize(`${i + 1}. ${step}`);
      const lines = doc.splitTextToSize(fullText, maxWidth);
      checkPage(LH * lines.length + 3);
      lines.forEach((line) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(30, 30, 30);
        checkPage(LH);
        doc.text(line, margin, y);
        y += LH;
      });
      y += 3;
    });

    // ── AI-Ready GDocP Checklist ──────────────────────────────────
    y += 10;
    doc.addPage(); y = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.text('AI-Ready GDocP Checklist for Small Regulated Labs', margin, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    const introLines = doc.splitTextToSize(
      'A practical checklist to help your lab assess whether documentation controls, data-integrity safeguards, review responsibilities, and AI-related workflows are ready for compliant use of AI in deviations, CAPAs, validation, SOPs, and other GxP-critical records.',
      maxWidth
    );
    introLines.forEach((l) => { doc.text(l, margin, y); y += LH; });
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text('How to use this checklist', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    const howLines = doc.splitTextToSize(
      'Use each item as Yes, No, or Not yet. If several items in one section are marked No or Not yet, treat that section as a control gap before AI use is expanded in regulated workflows.',
      maxWidth
    );
    howLines.forEach((l) => { checkPage(LH); doc.text(l, margin, y); y += LH; });
    y += 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text('What this checklist covers', margin, y);
    y += 7;
    const coverItems = [
      'Documentation control checkpoints grounded in GDocP and ALCOA+ principles.',
      'Review and approval questions to keep a human in the loop at the right steps.',
      'Data integrity and traceability checks for electronic records and AI-assisted outputs.',
      'AI workflow risk prompts covering context of use, oversight, and logging.',
      'Small-lab implementation reminders to support phased rollout instead of uncontrolled adoption.',
    ];
    coverItems.forEach((item) => {
      checkPage(LH + 2);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60);
      const ls = doc.splitTextToSize(`- ${item}`, maxWidth - 5);
      ls.forEach((l) => { doc.text(l, margin + 3, y); y += LH; });
    });
    y += 6;

    const SECTIONS = [
      {
        title: '1. Scope and document hierarchy',
        note: 'Fix document control basics before expanding AI to more workflows.',
        items: [
          'A clear list exists of GxP-critical documents and records where AI may be used, such as deviations, CAPAs, validation summaries, and SOPs.',
          'Each controlled document has an identified owner, status, and effective version.',
          'Only one effective version of each controlled document is available at the point of use.',
          'Document changes follow a documented change-control process with requester, approver, and rationale.',
          'AI is limited to defined workflow steps rather than being used informally across uncontrolled documents.',
        ],
      },
      {
        title: '2. ALCOA+ and GDocP baseline controls',
        note: 'Any No here suggests reinforcing basic GDocP before expanding AI use.',
        items: [
          'AI-assisted records remain attributable, showing who created, reviewed, and approved content.',
          'AI-assisted records remain legible, understandable, and linked to the relevant source context.',
          'Prior versions of AI-assisted drafts or controlled documents are retained and identifiable.',
          'Corrections and updates include reason for change and approver information.',
          'GxP-relevant records are backed up and retrievable for the required retention period.',
        ],
      },
      {
        title: '3. Review and approval responsibilities',
        note: 'If reviewers cannot point to an explicit approval step, flag that as a gap.',
        items: [
          'Each AI-assisted workflow has named reviewers and an explicit approval step.',
          'Reviewers know what they are responsible for checking, such as accuracy, references, data integrity, and risk language.',
          'SOPs or work instructions state where AI output cannot be used without human review or revision.',
          'Handoff points are defined, such as intake, AI draft, QA review, revision, and approval.',
          'Electronic approvals use unique user accounts, time stamps, and clear signing meaning.',
        ],
      },
      {
        title: '4. Data integrity and traceability',
        note: 'Treat any Not yet here like a core Part 11 control gap.',
        items: [
          'Access to AI tools used in regulated workflows is role-based and does not rely on shared accounts.',
          'An audit trail exists for key actions such as created, edited, reviewed, approved, and exported.',
          'AI-assisted content can be linked back to the source inputs used to create it.',
          'The organization can identify which model or tool version was used for a given document or period.',
          'Records are stored in a way that prevents silent overwrite and supports tamper-evident history.',
        ],
      },
      {
        title: '5. AI workflow risk prompts',
        note: '0-2 high-risk flags: proceed with pilot. 3+: address controls before scaling.',
        items: [
          'The context of use is defined for each AI workflow, including what AI does, what it does not do, and what records it affects.',
          'Each AI use case has been risk-rated based on product quality, patient impact, or regulatory significance.',
          'Higher-risk workflows require stronger controls such as second review or independent confirmation.',
          'AI input and output handling is logged or otherwise controlled for higher-risk use cases.',
          'A documented escalation path exists if AI output is incorrect, misleading, or incomplete in a GxP context.',
        ],
      },
      {
        title: '6. Validation and monitoring',
        note: 'Any No here is a reason to stay in pilot rather than routine GxP use.',
        items: [
          'AI tools used in GxP-relevant workflows are treated as computerized tools requiring risk-based validation or qualification.',
          'At least one validation activity exists, such as sample-output review, comparison against manual work, or a defined test set.',
          'Re-validation or re-qualification triggers are defined, such as model changes, workflow changes, or observed drift.',
          'Periodic review of AI performance, errors, or exceptions is scheduled.',
          'Staff are trained on AI-specific failure modes such as hallucinations, omissions, and escalation expectations.',
        ],
      },
      {
        title: '7. Small-lab implementation reminders',
        note: 'Start with one workflow and one tool. Expand only after controls are shown to work.',
        items: [
          'AI rollout begins with one workflow instead of broad deployment across all quality records.',
          'A simple internal risk tiering method is used to focus attention on higher-risk use cases first.',
          'Early AI workflows are chosen where strong human oversight is easy to maintain.',
          'Where GDocP expectations cannot yet be met, the workflow remains manual or semi-manual and the reason is documented.',
          'Decisions about AI rollout are captured in a short, version-controlled memo, SOP, or policy note.',
        ],
      },
    ];

    const COL_YES = pageWidth - margin - 60;
    const COL_NO = pageWidth - margin - 38;
    const COL_NOTYET = pageWidth - margin - 10;
    const CHECKBOX = '[ ]';

    SECTIONS.forEach((section) => {
      checkPage(30);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(sanitize(section.title), margin, y);
      y += 8;

      // Header row
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text('Yes', COL_YES, y, { align: 'center' });
      doc.text('No', COL_NO, y, { align: 'center' });
      doc.text('Not yet', COL_NOTYET, y, { align: 'right' });
      y += 5;
      doc.setDrawColor(200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      section.items.forEach((item) => {
        const itemLines = doc.splitTextToSize(sanitize(item), COL_YES - margin - 6);
        const rowHeight = itemLines.length * LH + 4;
        checkPage(rowHeight + 4);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(40);
        itemLines.forEach((l, li) => {
          doc.text(l, margin, y + li * LH);
        });

        const midY = y + (itemLines.length * LH) / 2 - 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text(CHECKBOX, COL_YES, midY, { align: 'center' });
        doc.text(CHECKBOX, COL_NO, midY, { align: 'center' });
        doc.text(CHECKBOX, COL_NOTYET, midY, { align: 'right' });

        y += rowHeight;
        doc.setDrawColor(230);
        doc.line(margin, y - 1, pageWidth - margin, y - 1);
      });

      // Section note
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(120);
      const noteLines = doc.splitTextToSize(`Note: ${sanitize(section.note)}`, maxWidth);
      checkPage(noteLines.length * 5 + 8);
      noteLines.forEach((l) => { doc.text(l, margin, y); y += 5; });
      y += 8;
    });

    // Scoring summary
    checkPage(50);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('Scoring Summary', margin, y);
    y += 8;
    const scoringItems = [
      { label: 'Strong foundation:', text: 'Most answers are Yes, and no section has multiple high-risk gaps.' },
      { label: 'Needs targeted remediation:', text: 'Several items are No or Not yet, but gaps are concentrated in a few sections that can be corrected before expansion.' },
      { label: 'Keep AI in pilot or sandbox:', text: 'Multiple sections show basic control gaps around review, traceability, or validation. AI should not yet scale into routine regulated use.' },
    ];
    scoringItems.forEach((si) => {
      checkPage(14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(40);
      doc.text(sanitize(si.label), margin, y);
      y += LH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60);
      const ls = doc.splitTextToSize(sanitize(si.text), maxWidth - 4);
      ls.forEach((l) => { checkPage(LH); doc.text(l, margin + 4, y); y += LH; });
      y += 4;
    });

    y += 4;
    checkPage(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('Priority Actions If Gaps Appear', margin, y);
    y += 8;
    const priorityActions = [
      'Fix document control basics before expanding AI to more workflows.',
      'Define explicit human review and approval points before relying on AI-assisted outputs.',
      'Strengthen traceability, logging, and version visibility for AI-assisted records.',
      'Validate one workflow first, then expand only after controls are shown to work in practice.',
    ];
    priorityActions.forEach((pa) => {
      checkPage(LH + 2);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60);
      const ls = doc.splitTextToSize(sanitize(`- ${pa}`), maxWidth - 5);
      ls.forEach((l) => { doc.text(l, margin + 3, y); y += LH; });
    });

    y += 8;
    checkPage(16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    const disclaimer = doc.splitTextToSize(
      'DISCLAIMER: This AI-generated summary is informational only and does not constitute regulatory, legal, or compliance advice. All validation decisions must be made by qualified QA/regulatory professionals.',
      maxWidth
    );
    disclaimer.forEach((line) => { checkPage(5); doc.text(line, margin, y); y += 5; });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(
        `Page ${i} of ${totalPages}  |  AI-Generated Draft — Informational Only`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 6,
        { align: 'center' }
      );
    }

    doc.save(`Validation-Assessment-${name.replace(/\s+/g, '-')}.pdf`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-28 pb-24"
    >
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Your AI Validation Summary</h1>
            <p className="text-sm text-muted-foreground">Prepared for {name} · Informational draft only</p>
          </div>
        </div>

        <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 leading-relaxed">
            This AI-generated summary is <strong>informational only</strong> and does not constitute regulatory, legal, or compliance advice. All validation decisions must be made by qualified QA/regulatory professionals.
          </p>
        </div>

        <div className="bg-card rounded-2xl border border-border p-7 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileCheck className="w-4 h-4 text-primary" />
            <h2 className="text-base font-semibold text-foreground">Validation Status Summary</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap text-sm">{result.draftSummary}</p>
        </div>

        <div className="bg-card rounded-2xl border border-border p-7 mb-8">
          <div className="flex items-center gap-2 mb-5">
            <ChevronRight className="w-4 h-4 text-secondary" />
            <h2 className="text-base font-semibold text-foreground">Suggested Next Steps</h2>
          </div>
          <ol className="space-y-3">
            {nextSteps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-muted-foreground leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-6">
          <Button variant="outline" size="lg" className="gap-2" onClick={exportToPDF}>
            <Download className="w-4 h-4" />
            Export Summary as PDF
          </Button>
        </div>

        <div className="bg-gradient-to-br from-primary/5 to-accent/30 rounded-2xl border border-border p-7 text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">Ready to Get Expert Help?</h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            This draft is a starting point. Book a free discovery call and we'll review your actual validation package together.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/Contact">
              <Button size="lg" className="gap-2">
                Book Your Discovery Call
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <a href="mailto:hello@labcomplyx.com">
              <Button variant="outline" size="lg" className="gap-2">
                <Mail className="w-4 h-4" />
                Email Us Directly
              </Button>
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            A copy of this summary has been sent to our team and we'll follow up shortly.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function ValidationIntake() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    orgType: "",
    validationType: "",
    description: "",
    resultsText: "",
    primaryChallenge: "",
    dataReadiness: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploadResult = await base44.integrations.Core.UploadPrivateFile({ file });
      setUploadedFile({ name: file.name, url: uploadResult.file_uri });
      const isAuth = await base44.auth.isAuthenticated();
      if (isAuth) {
        try {
          setParsing(true);
          const signed = await base44.integrations.Core.CreateFileSignedUrl({ file_uri: uploadResult.file_uri, expires_in: 300 });
          const parseResult = await base44.functions.invoke("parseValidationDocument", { file_url: signed.signed_url });
          if (parseResult.data && !parseResult.data.error) {
            const extracted = parseResult.data;
            setForm(prev => ({
              ...prev,
              orgType: extracted.orgType || prev.orgType,
              validationType: extracted.validationType || prev.validationType,
              description: extracted.description || prev.description,
              resultsText: extracted.resultsText || prev.resultsText,
              primaryChallenge: extracted.primaryChallenge || prev.primaryChallenge,
              dataReadiness: extracted.dataReadiness || prev.dataReadiness,
            }));
          }
        } catch (parseError) {
          console.warn("Auto-parse failed (non-fatal):", parseError.message);
        } finally {
          setParsing(false);
        }
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
      setParsing(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) handleFileUpload(e.target.files[0]);
  };

  const removeUploadedFile = () => setUploadedFile(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.validationType || !form.description) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        org_type: form.orgType,
        validation_type: form.validationType,
        description: form.description,
        results_text: form.resultsText,
        primary_challenge: form.primaryChallenge,
        data_readiness: form.dataReadiness,
        uploaded_file_url: uploadedFile?.url || null,
        home_variant: getVariant(),
        experiment_visitor_id: getVariant() ? getVisitorId() : null,
      };
      const attribution = getAttribution();
      const ctaSource = new URLSearchParams(window.location.search).get("src") || "direct";
      base44.analytics.track({ eventName: "compliance_review_submit_attempted", properties: { cta_source: ctaSource, ...attribution } });
      const recaptcha_token = await getRecaptchaToken("validation_intake");
      const res = await base44.functions.invoke("validationIntake", { ...payload, recaptcha_token });
      setResult(res.data);
      base44.analytics.track({ eventName: "validation_assessment_completed", properties: { validation_type: form.validationType, org_type: form.orgType || "unknown" } });
      base44.analytics.track({ eventName: "compliance_review_submitted", properties: { cta_source: ctaSource, workflow_type: form.validationType, ...attribution } });
      logExperimentEvent(base44, "review_submitted", { traffic_source: attribution.traffic_source, device_type: attribution.device_type });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Validation intake error:", err);
      toast.error("Something went wrong generating your assessment. Please try again or contact hello@labcomplyx.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ResultView result={result} name={form.name} formData={form} />;

  return (
    <>
      <SEO
        title="Free AI Validation Assessment for Small Regulated Labs | LabComplyx"
        description="Find out where AI can safely support your validation, QA, and documentation workflows — and where stronger controls, review steps, or audit evidence are still needed. Free for small biotech, diagnostics, and pharma labs."
        keywords="free AI validation assessment, AI workflow compliance check, regulated lab AI readiness, CAPA deviation AI review, small lab QA AI, GxP AI governance assessment, free lab compliance tool"
        canonical="https://labcomplyx.com/validation-intake"
        ogUrl="https://labcomplyx.com/validation-intake"
        noIndex={true}
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Free AI Assessment", url: "https://labcomplyx.com/validation-intake" },
        ]}
      />

      {/* Landing sections */}
      <ValidationHero onGetStarted={scrollToForm} />
      <ValidationAIImpact />
      <ValidationWhatYouGet />
      <ValidationWhoItsFor />
      <ValidationEvalProcess />

      {/* Form section */}
      <section ref={formRef} className="py-16 sm:py-20 bg-background border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
              Book a Free Compliance Review
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-5">
              Tell us which workflow is giving you trouble. It takes a couple of minutes.
            </p>
            <IntakeExpectations />
            <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-relaxed">
                <strong>Informational only.</strong> This tool produces non-binding AI drafts. All compliance decisions require review by qualified QA/regulatory professionals.
              </p>
            </div>
          </div>

          {/* File Upload */}
          <div className="bg-card rounded-2xl border border-border p-8 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Upload Existing Validation Document (Optional)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a protocol PDF, data spreadsheet, or validation report to automatically pre-fill the form below. Our AI will extract relevant details to save you time.
            </p>
            {!uploadedFile ? (
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  className="hidden"
                  accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.png,.jpg,.jpeg"
                  onChange={handleFileInputChange}
                  disabled={uploading || parsing}
                />
                {uploading || parsing ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-sm font-medium text-foreground">
                      {uploading ? "Uploading document..." : "Parsing content with AI..."}
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">Drag and drop your file here, or click to browse</p>
                    <p className="text-xs text-muted-foreground">Supports PDF, Excel, Word, and image files</p>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-secondary/5 border border-secondary/20 rounded-xl">
                <FileText className="w-5 h-5 text-secondary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-muted-foreground">Document uploaded and parsed</p>
                </div>
                <button type="button" onClick={removeUploadedFile} className="p-1 hover:bg-muted rounded-md transition-colors">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-8 space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-medium text-foreground mb-1.5">Full Name *</label>
                <Input required placeholder="Jane Smith" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
              </div>
              <div>
                <label className="block font-medium text-foreground mb-1.5">Work Email *</label>
                <Input type="email" required placeholder="jane@yourlab.com" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-medium text-foreground mb-1.5">Lab / Organization Type</label>
                <Select value={form.orgType} onValueChange={(v) => handleChange("orgType", v)}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-medium text-foreground mb-1.5">Validation / Workflow Type *</label>
                <Select value={form.validationType} onValueChange={(v) => handleChange("validationType", v)} required>
                  <SelectTrigger><SelectValue placeholder="Select validation type" /></SelectTrigger>
                  <SelectContent>
                    {VALIDATION_TYPES.map((v) => (<SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1.5">Current Workflow or Validation Challenge *</label>
              <Textarea
                required
                rows={4}
                placeholder="Describe the workflow, documentation challenge, or validation project. Include scope, current phase, and any known gaps or issues."
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </div>

            <details className="group rounded-xl border border-border px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-foreground">Add more detail (optional)</summary>
              <div className="space-y-6 mt-5">
            <div>
              <label className="block font-medium text-foreground mb-1.5">
                Current Use of AI, If Any <span className="text-muted-foreground font-normal text-xs">(optional)</span>
              </label>
              <Textarea
                rows={3}
                placeholder="Are you already using AI anywhere in your quality or documentation workflows? If so, how? Paste any relevant results, notes, or concerns."
                value={form.resultsText}
                onChange={(e) => handleChange("resultsText", e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1.5">Biggest Concern</label>
              <Textarea
                rows={3}
                placeholder="What is your primary concern — speed, review controls, compliance, traceability, or audit readiness?"
                value={form.primaryChallenge}
                onChange={(e) => handleChange("primaryChallenge", e.target.value)}
              />
            </div>

            <div>
              <label className="block font-medium text-foreground mb-1.5">Data Readiness for AI</label>
              <Select value={form.dataReadiness} onValueChange={(v) => handleChange("dataReadiness", v)}>
                <SelectTrigger><SelectValue placeholder="How ready is your data for AI analysis?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clean">Clean and structured</SelectItem>
                  <SelectItem value="some_cleaning">Some cleaning needed</SelectItem>
                  <SelectItem value="raw">Raw and disparate</SelectItem>
                  <SelectItem value="unsure">Unsure</SelectItem>
                </SelectContent>
              </Select>
            </div>
              </div>
            </details>

            <Button
              type="submit"
              size="lg"
              className="w-full gap-2 text-base"
              disabled={submitting || !form.validationType || !form.name || !form.email || !form.description}
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Generating your assessment…</>
              ) : (
                <><Sparkles className="w-4 h-4" />Get My Free Assessment</>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Your information is used only to deliver the assessment and relevant follow-up about LabComplyx services and resources.
            </p>
          </form>
        </div>
      </section>

      <ValidationNextSteps />
    </>
  );
}